import { useApiSuperuserExecuteQuery } from '@repo/api-codegen';
import type { QueryExecutionOut } from '@repo/api-codegen/v2';
import { useApiSuperuserGetQueryBackgroundJob } from '@repo/api-codegen/v2';
import { trackEvent } from '@repo/common/analytics';
import { createMockableHook } from '@repo/common/utils/createMockableHook';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../../../network';
import type { ExecutionStatus } from '../../../components/Sql/types';
import { fetchExecutionStatusWithRetry } from '../../../components/Sql/utils';
import queryClient from '../../queryClient';
import type { IMetric } from '../../types';
import { useAuthUser } from '../authUser';
import { metricsQueryKeyFactory } from '../metricTerm';
import { useCreateMetricExecution } from '../metricTerm/useCreateMetricExecution';
import { useMetricRefresh } from '../metricTerm/useMetricRefresh';

function useMetricExecutionInternal(
	metric: IMetric,
	eventPrefix: string = 'metric/embedded',
	onExecutionEnd?: () => void
) {
	const [isExecuting, setIsExecuting] = useState(false);
	const [executionStatus, setExecutionStatus] =
		useState<ExecutionStatus | null>(null);
	const { user, workspace } = useAuthUser();
	const { mutateAsync: createExecution } = useCreateMetricExecution();

	const watchExecution = useCallback(
		async (executionId: string) => {
			setIsExecuting(true);
			setExecutionStatus(null);

			const url = `${api()}metric/executions/${executionId}/`;
			const newExecutionStatus = await fetchExecutionStatusWithRetry(url, 1);

			setExecutionStatus(newExecutionStatus);
			setIsExecuting(false);

			// refetch metric data
			await queryClient.refetchQueries({
				queryKey: metricsQueryKeyFactory.byId(metric.id),
			});
			await queryClient.refetchQueries({
				queryKey: [...metricsQueryKeyFactory.all(), 'get_or_create', metric.id],
			});

			onExecutionEnd?.();
		},
		[metric.id, onExecutionEnd]
	);

	const execute = useCallback(async () => {
		if (isExecuting) {
			return;
		}

		trackEvent(
			`${eventPrefix}/run`,
			{
				id: metric.id,
			},
			user,
			workspace
		);

		const { id: newExecutionId } = await createExecution({
			metricId: metric.id,
		});

		await watchExecution(newExecutionId);
	}, [
		createExecution,
		eventPrefix,
		isExecuting,
		metric.id,
		user,
		watchExecution,
		workspace,
	]);

	const refreshMetric = useMetricRefresh(metric.id);

	useEffect(() => {
		async function refresh() {
			if (!metric?.scheduled_delta) {
				return;
			}

			// we let backend decide if the metric needs to be refreshed or not
			// this should be ok to be called multiple times
			const refreshExecutionData = await refreshMetric();
			if (
				!!refreshExecutionData?.id &&
				!['completed', 'failed'].includes(refreshExecutionData.status)
			) {
				// if execution is not completed or failed, we watch it
				await watchExecution(refreshExecutionData.id);
			}
		}

		refresh();
	}, [metric, refreshMetric, watchExecution]);

	return {
		isExecuting,
		executionStatus,
		execute,
	};
}

export const [useMetricExecution, MockUseMetricExecutionInternalProvider] =
	createMockableHook(useMetricExecutionInternal);

export function useSuperuserQueryExecution() {
	const [isExecuting, setIsExecuting] = useState(false);
	const [executionStatus, setExecutionStatus] =
		useState<QueryExecutionOut | null>(null);
	const [executionId, setExecutionId] = useState<string | null>(null);

	const { mutateAsync: executeQuery } = useApiSuperuserExecuteQuery();

	// Refetch execution status loop
	const { data: queryData, error: queryError } =
		useApiSuperuserGetQueryBackgroundJob(
			{
				pathParams: { executionId: executionId || '' },
			},
			{
				enabled: !!executionId,
				refetchInterval(query) {
					const { data, error } = query.state;
					// Handle error case - stop refetching and log the error
					if (error) {
						return false;
					}

					// Stop refetching if query is completed or failed
					if (data?.completed || data?.failed) {
						return false;
					}

					return 1000;
				},
			}
		);

	// Handle data updates
	if (queryData && queryData !== executionStatus) {
		setExecutionStatus(queryData);
		if (queryData.completed || queryData.failed) {
			setIsExecuting(false);
		}
	}

	// Handle error updates
	if (queryError && !executionStatus?.failed) {
		setExecutionStatus({
			failed: true,
			logs: queryError.payload || 'Query execution failed',
			started: true,
			completed: false,
			results: [],
			column_types: {},
		});
		setIsExecuting(false);
	}

	const execute = useCallback(
		async (sql: string, integrationId?: string) => {
			if (isExecuting) {
				return;
			}

			setIsExecuting(true);
			setExecutionStatus(null);
			setExecutionId(null);

			const { job_id: id } = await executeQuery({
				body: {
					integration_id: integrationId,
					sql: sql,
				},
			});
			setExecutionId(id);
		},
		[executeQuery, isExecuting]
	);

	return {
		execute,
		isExecuting,
		executionStatus,
	};
}
